import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload } from "lucide-react";
import { api, API, errorText, formatPhone } from "../lib/api";
import { SERVICES } from "../data/services";
import { SERVICE_LABELS, useAuth } from "./AuthContext";
import { useSettings } from "../context/SettingsContext";

const Card = ({ title, children, testId }) => (
  <section className="panel p-6 space-y-4" data-testid={testId}>
    <h2 className="text-xl font-bold uppercase">{title}</h2>
    {children}
  </section>
);

const Text = ({ label, value, onChange, testId, hint, ...rest }) => (
  <label className="block text-sm">
    <span className="font-semibold">{label}</span>
    <input className="tow-input mt-1" value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} {...rest} />
    {hint && <span className="mt-1 block text-xs text-gray-500">{hint}</span>}
  </label>
);

const Toggle = ({ label, checked, onChange, testId, hint }) => (
  <label className="flex items-start gap-3 text-sm cursor-pointer">
    <input type="checkbox" className="mt-0.5 h-5 w-5 accent-[#FACC15]" checked={!!checked} onChange={(e) => onChange(e.target.checked)} data-testid={testId} />
    <span><span className="font-semibold">{label}</span>{hint && <span className="block text-xs text-gray-500">{hint}</span>}</span>
  </label>
);

const LogoUpload = ({ s, setS }) => {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/settings/logo", fd);
      setS(data);
      toast.success("Logo updated");
    } catch (err) { toast.error(errorText(err)); } finally { setBusy(false); }
  };
  const remove = async () => setS((await api.delete("/admin/settings/logo")).data);
  return (
    <div className="flex flex-wrap items-center gap-4">
      {s.has_logo ? <img src={`${API}/public/logo?v=${s.logo_version}`} alt="Current logo" className="h-14 max-w-[180px] rounded bg-black object-contain" /> : <span className="text-sm text-gray-400">Using the built-in tow truck logo.</span>}
      <label className="btn-ghost cursor-pointer px-4 py-2 text-sm">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload logo
        <input type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" onChange={onFile} data-testid="admin-logo-upload" />
      </label>
      {s.has_logo && <button type="button" onClick={remove} className="text-sm text-red-400 hover:underline" data-testid="admin-logo-remove">Remove</button>}
    </div>
  );
};

export default function SettingsPage() {
  const { user } = useAuth();
  const { reload } = useSettings();
  const [s, setS] = useState(null);
  const [zips, setZips] = useState("");
  const [integ, setInteg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => { setS(r.data); setZips((r.data.service_zips || []).join(", ")); });
    api.get("/admin/integrations").then((r) => setInteg(r.data));
  }, []);

  if (!s) return <Loader2 className="h-6 w-6 animate-spin text-[#FACC15]" />;
  const set = (k) => (v) => setS({ ...s, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...s, service_zips: zips.split(/[\s,]+/).filter(Boolean) };
      const { data } = await api.put("/admin/settings", body);
      setS(data);
      setZips((data.service_zips || []).join(", "));
      api.get("/admin/integrations").then((r) => setInteg(r.data));
      reload();
      toast.success("Settings saved");
    } catch (e) { toast.error(errorText(e)); } finally { setSaving(false); }
  };

  const purge = async () => {
    if (!window.confirm(`Delete completed/cancelled requests older than ${s.retention_days} days? This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/admin/retention/purge");
      toast.success(`Deleted ${data.deleted} old request(s)`);
    } catch (e) { toast.error(errorText(e)); }
  };

  const toggleService = (id, on) => set("enabled_services")(on ? [...s.enabled_services, id] : s.enabled_services.filter((x) => x !== id));
  const status = (ok, yes, no) => <span className={ok ? "text-emerald-300" : "text-yellow-300"}>{ok ? yes : no}</span>;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-settings-page">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-3xl sm:text-4xl font-extrabold uppercase">Business settings</h1>
        <button type="button" onClick={save} disabled={saving} className="btn-yellow px-6 py-3 text-sm" data-testid="admin-settings-save-btn">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save settings</button>
      </div>

      <Card title="Business profile" testId="settings-profile">
        <div className="grid gap-4 sm:grid-cols-2">
          <Text label="Business name" value={s.business_name} onChange={set("business_name")} testId="settings-business-name" />
          <Text label="Business phone" type="tel" value={s.phone ? formatPhone(s.phone) : ""} onChange={set("phone")} testId="settings-phone" placeholder="Phone number coming soon" hint={s.phone ? "Shown on the website with CALL NOW and TEXT US." : "Phone number coming soon — CALL NOW is disabled until you add it."} />
          <Text label="Public business email" type="email" value={s.email} onChange={set("email")} testId="settings-email" placeholder="Not configured yet" hint="Shown in the footer and used as reply-to on emails." />
          <Text label="Website domain" value={s.site_url} onChange={set("site_url")} testId="settings-site-url" placeholder="https://www.yourdomain.com" hint="Used for the sitemap and search data once your custom domain is connected." />
        </div>
        <p className="text-sm font-semibold">Logo</p>
        <LogoUpload s={s} setS={setS} />
      </Card>

      <Card title="Hours & service area" testId="settings-hours">
        <Toggle label="I confirm we are available 24 hours a day, 7 days a week" checked={s.is_24_7_confirmed} onChange={set("is_24_7_confirmed")} testId="settings-247-toggle" hint="Only when this is on does the website say “24/7”." />
        <Text label="Business hours (shown when 24/7 is off)" value={s.hours_text} onChange={set("hours_text")} testId="settings-hours-text" placeholder="e.g. Mon–Sat 7am–10pm" />
        <div className="grid gap-4 sm:grid-cols-3">
          <Text label="Service area description" value={s.service_area_text} onChange={set("service_area_text")} testId="settings-area-text" />
          <Text label="City" value={s.city} onChange={set("city")} testId="settings-city" />
          <Text label="State" value={s.state} onChange={set("state")} testId="settings-state" />
        </div>
        <label className="block text-sm"><span className="font-semibold">ZIP codes served (optional)</span>
          <textarea className="tow-input mt-1" rows={2} value={zips} onChange={(e) => setZips(e.target.value)} placeholder="77002, 77003, 77004" data-testid="settings-zips" />
          <span className="mt-1 block text-xs text-gray-500">Separate with commas. Shown publicly only if you add them.</span>
        </label>
      </Card>

      <Card title="Services offered" testId="settings-services">
        <p className="text-sm text-gray-400">Only checked services appear on the website and in the request form.</p>
        <div className="grid gap-3 sm:grid-cols-2">
          {SERVICES.map((x) => <Toggle key={x.id} label={SERVICE_LABELS[x.id]} checked={s.enabled_services.includes(x.id)} onChange={(on) => toggleService(x.id, on)} testId={`settings-service-${x.id}`} />)}
        </div>
      </Card>

      <Card title="Quotes & pricing" testId="settings-pricing">
        <Toggle label="Show prices on the website" checked={s.show_prices} onChange={set("show_prices")} testId="settings-show-prices" hint="Off by default. Nothing is shown unless you enter prices." />
        <div className="grid gap-4 sm:grid-cols-2">
          <Text label="Service call fee ($)" type="number" min="0" step="0.01" value={s.service_call_fee ?? ""} onChange={set("service_call_fee")} testId="settings-service-fee" />
          <Text label="Mileage rate ($ per mile)" type="number" min="0" step="0.01" value={s.mileage_rate ?? ""} onChange={set("mileage_rate")} testId="settings-mileage-rate" />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {SERVICES.map((x) => (
            <Text key={x.id} label={`${SERVICE_LABELS[x.id]} — starting price ($)`} type="number" min="0" step="0.01" value={s.service_prices?.[x.id] ?? ""} onChange={(v) => set("service_prices")({ ...s.service_prices, [x.id]: v })} testId={`settings-price-${x.id}`} />
          ))}
        </div>
        <Text label="Pricing note" value={s.pricing_note} onChange={set("pricing_note")} testId="settings-pricing-note" placeholder="e.g. Final price confirmed before work begins." />
      </Card>

      <Card title="Notifications" testId="settings-notifications">
        {integ && (
          <ul className="space-y-1 text-sm" data-testid="settings-integration-status">
            <li>Email provider: {status(integ.email_provider_ready, "Ready (managed email)", "Not configured")}</li>
            <li>SMS provider: {status(integ.sms_provider_ready, "Ready (Twilio)", "Not configured — add Twilio keys")}</li>
          </ul>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <Text label="Send new-request alerts to email" type="email" value={s.notification_email} onChange={set("notification_email")} testId="settings-notification-email" placeholder="Not set" />
          <Text label="Send new-request alerts to mobile (SMS)" type="tel" value={s.notification_phone ? formatPhone(s.notification_phone) : ""} onChange={set("notification_phone")} testId="settings-notification-phone" placeholder="Not set" />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Toggle label="Email me about new requests" checked={s.notify_owner_email} onChange={set("notify_owner_email")} testId="settings-notify-owner-email" />
          <Toggle label="Text me about new requests" checked={s.notify_owner_sms} onChange={set("notify_owner_sms")} testId="settings-notify-owner-sms" />
          <Toggle label="Email customers a confirmation (if they gave an email)" checked={s.notify_customer_email} onChange={set("notify_customer_email")} testId="settings-notify-customer-email" />
          <Toggle label="Text customers a confirmation (if they chose text)" checked={s.notify_customer_sms} onChange={set("notify_customer_sms")} testId="settings-notify-customer-sms" />
        </div>
      </Card>

      <Card title="Maps & language" testId="settings-maps">
        <Toggle label="Enable map, GPS address lookup and address suggestions (OpenStreetMap)" checked={s.maps_enabled} onChange={set("maps_enabled")} testId="settings-maps-toggle" hint="No key needed. When off, customers type the address manually." />
        <label className="block text-sm"><span className="font-semibold">Default website language</span>
          <select className="tow-input mt-1 w-auto" value={s.default_language} onChange={(e) => set("default_language")(e.target.value)} data-testid="settings-default-language">
            <option value="en">English</option><option value="es">Español</option>
          </select>
        </label>
      </Card>

      <Card title="Privacy & data retention" testId="settings-retention">
        <Text label="Keep completed/cancelled requests for (days)" type="number" min="30" max="3650" value={s.retention_days} onChange={set("retention_days")} testId="settings-retention-days" />
        {user?.role === "owner" && <button type="button" onClick={purge} className="rounded-full border border-red-500/60 px-4 py-2 text-sm text-red-300 hover:bg-red-500/10" data-testid="settings-purge-btn">Delete old requests now</button>}
      </Card>

      <button type="button" onClick={save} disabled={saving} className="btn-yellow px-6 py-3 text-sm" data-testid="admin-settings-save-btn-bottom">Save settings</button>
    </div>
  );
}
